import React, { useEffect, useState, useMemo } from "react";
import { decode } from "html-entities";
import Header from "../components/Header";
import Footer from "../components/Footer";
import GlobalCta from "../components/global/GlobalCta";
import PageHero from "../components/global/PageHero";
import BlogFeature from "../components/blog/BlogFeature";
import BlogGrid from "../components/blog/BlogGrid";
import { fetchApi } from "../config";

const POSTS_PER_PAGE = 7;

function getPaginationItems(pageCount, currentPage) {
    if (pageCount <= 7) {
        return Array.from({ length: pageCount }, (_, index) => index + 1);
    }

    if (currentPage <= 4) {
        return [1, 2, 3, 4, 5, "ellipsis", pageCount];
    }

    if (currentPage >= pageCount - 3) {
        return [1, "ellipsis", pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount];
    }

    return [1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", pageCount];
}

export default function Blog() {
    const [posts, setPosts] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        let mounted = true;
        async function load() {
            try {
                const res = await fetchApi("get_posts.php");
                if (!mounted) return;
                if (res && Array.isArray(res.Posts)) {
                    setPosts(res.Posts);
                } else {
                    setPosts([]);
                }
            } catch (err) {
                setPosts([]);
            }
        }
        load();
        return () => { mounted = false; };
    }, []);

    // Build unique category list from posts (dedupe by category_id when available)
    const categories = useMemo(() => {
        if (!Array.isArray(posts)) return [];
        const map = new Map();
        for (const p of posts) {
            const rawName = (p.category_name || '').toString().trim();
            if (!rawName) continue; // skip posts without a category name
            if (rawName.toLowerCase() === 'uncategorized') continue; // skip Uncategorized
            const id = p.category_id != null ? String(p.category_id) : `name:${rawName}`;
            const name = rawName;
            if (!map.has(id)) map.set(id, { id, name });
        }
        return [{ id: 'all', name: 'All' }, ...Array.from(map.values())];
    }, [posts]);

    // Filter posts client-side when a category is selected
    const filtered = useMemo(() => {
        if (!Array.isArray(posts)) return [];
        if (!selectedCategory || selectedCategory === 'all') return posts;
        return posts.filter(p => {
            if (p.category_id != null) return String(p.category_id) === selectedCategory;
            return (`name:${p.category_name || 'Uncategorized'}`) === selectedCategory;
        });
    }, [posts, selectedCategory]);

    const pageCount = Math.ceil(filtered.length / POSTS_PER_PAGE);
    const paginationItems = getPaginationItems(pageCount, currentPage);
    const pagePosts = filtered.slice((currentPage - 1) * POSTS_PER_PAGE, currentPage * POSTS_PER_PAGE);
    const feature = pagePosts.length ? pagePosts[0] : null;
    const gridPosts = pagePosts.length > 1 ? pagePosts.slice(1) : null;
    const changePage = (page) => {
        setCurrentPage(page);
        document.querySelector(".blog-filter")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    };

    return (
        <>
            <Header />

            <main>
                <PageHero eyebrow="Insights" title="Notes on building better software.">
                    Practical thinking on custom development, product and the web — from the team.
                </PageHero>

                <div className="container">
                    <div className="blog-filter">
                        <label htmlFor="blog-category">Filter by category:</label>
                        <select id="blog-category" className="category-select" value={selectedCategory} onChange={(e) => {
                            setSelectedCategory(e.target.value);
                            setCurrentPage(1);
                        }}>
                            {categories.map(c => (
                                <option key={c.id} value={c.id}>{decode(c.name)}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <BlogFeature post={feature} />

                <BlogGrid posts={gridPosts} showPlaceholder={posts === null} />

                {pageCount > 1 && (
                    <nav className="container blog-pagination" aria-label="Blog pagination">
                        <button
                            type="button"
                            className="blog-page-button"
                            onClick={() => changePage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            Previous
                        </button>
                        <div className="blog-page-numbers">
                            {paginationItems.map((item, index) => (
                                item === "ellipsis" ? (
                                    <span className="blog-page-ellipsis" key={`ellipsis-${index}`} aria-label="More pages">
                                        …
                                    </span>
                                ) : (
                                    <button
                                        type="button"
                                        className={`blog-page-button${currentPage === item ? " is-active" : ""}`}
                                        key={item}
                                        onClick={() => changePage(item)}
                                        aria-label={`Page ${item}`}
                                        aria-current={currentPage === item ? "page" : undefined}
                                    >
                                        {item}
                                    </button>
                                )
                            ))}
                        </div>
                        <button
                            type="button"
                            className="blog-page-button"
                            onClick={() => changePage(currentPage + 1)}
                            disabled={currentPage === pageCount}
                        >
                            Next
                        </button>
                    </nav>
                )}
            </main>

            <GlobalCta />

            <Footer />
        </>
    );
}
